import React, { useRef, useEffect, useState } from "react";
import { Button, Card, CardHeader, Col, Collapse, Row } from "react-bootstrap";
import * as d3 from "d3";
import { Tabulator, PageModule, DownloadModule, ExportModule } from "tabulator-tables";
import "tabulator-tables/dist/css/tabulator.min.css";
import { MultiSelectAsync } from "./MultiSelectAsync";
import { SortModule } from "tabulator-tables";

interface Entity {
  entity_id: number;
  entity_name: string;
  fiscal_year: number;
  agg_type: string;
  agg1: string;
  net_amount: number;
}

interface EntityExpensesChartAndTableProps {
  chartLevel: "fund" | "organization" | "category";
  selectedEntityId: number | null; // Added null option
  formatCurrency: (value: number) => string;
  formatEntityString: (value: string) => string;
  data: Entity[];
  loading?: boolean;
  activeTab?: string; // Added to fix verticalFillMode issues
  entityName?: string; // Added to show entity name under chart 
}

const EntityExpensesChartAndTable: React.FC<EntityExpensesChartAndTableProps> = ({
  chartLevel,
  selectedEntityId,
  formatCurrency,
  formatEntityString,
  data,
  loading,
  activeTab, // Added to fix verticalFillMode issues
  entityName, // Added to show entity name under chart
}) => {
  const [options, setOptions] = useState<string[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [showHeaderInfo, setShowHeaderInfo] = useState<boolean>(false);
  const chartRef = useRef<SVGSVGElement>(null);
  const legendRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const tabulatorRef = useRef<InstanceType<typeof Tabulator> | null>(null);
  const tableRef = useRef<HTMLDivElement>(null);

  const formatTextForFileDownload = (chartLevel: string, entityName: string) => {
    const textFormatForDownload = `${entityName}_${chartLevel}_expenses`;
    return `${entityName}_${chartLevel}_expenses`;
  }

  // Added to get entity name updated
  useEffect(() => {
    // console.log("Entity name updated:", entityName);
  }, [entityName]);

  useEffect(() => {
    if (!data.length) return;
    const newOptions = Array.from(new Set(data.map(d => d.agg1).filter(d => d !== "NOT SPECIFIED"))).sort();
    setOptions(newOptions);
    setSelectedOptions(newOptions);
  }, [data, chartLevel]);

  // Updated Render D3 Chart
  useEffect(() => {
    if (loading || !data.length || !chartRef.current || !selectedOptions.length) return;

    const svg = d3.select(chartRef.current);
    svg.selectAll("*").remove();

    const filteredData = data.filter(d => selectedOptions.includes(d.agg1));
    if (!filteredData.length) return;

    const margin = { top: 10, right: 10, bottom: 60, left: 80 };
    const containerWidth = containerRef.current?.clientWidth ?? 1200;
    const width = containerWidth - margin.left - margin.right;
    const height = containerWidth * 0.6 - margin.top - margin.bottom;

    const categories = Array.from(new Set(filteredData.map(d => d.agg1).filter(d => d !== "NOT SPECIFIED")));

    const yearMap: Record<string, any> = {};
    filteredData.forEach(d => {
      const key = String(d.fiscal_year);
      if (!yearMap[key]) {
        yearMap[key] = { fiscal_year: d.fiscal_year };
        categories.forEach(cat => (yearMap[key][cat] = 0));
      }
      if (d.agg1 !== "NOT SPECIFIED") yearMap[key][d.agg1] += d.net_amount;
    });

    const pivotedData = Object.values(yearMap);

    const xScale = d3
      .scaleBand()
      .domain(pivotedData.map(d => String(d.fiscal_year)))
      .range([0, width])
      .padding(0.2);

    const yMax = d3.max(pivotedData, d => d3.sum(categories, cat => d[cat])) || 0;
    const yScale = d3.scaleLinear().domain([0, yMax]).nice().range([height, 0]);

    const color = d3
      .scaleOrdinal<string>()
      .domain(categories)
      .range(d3.schemeCategory10.concat(d3.schemePaired).concat(d3.schemeSet3));

    const stack = d3.stack<Record<string, any>>().keys(categories).order(d3.stackOrderNone).offset(d3.stackOffsetNone);
    const series = stack(pivotedData);

    svg.append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`)
      .call(
        d3.axisLeft(yScale)
          .tickFormat(d3.format("$,~"))
          .tickSize(-width)
      )
      .selectAll("line")
      .attr("stroke", "gray")
      .attr("stroke-dasharray", "7 7");

    // X-axis
    svg.append("g")
      .attr("transform", `translate(${margin.left},${height + margin.top})`)
      .call(d3.axisBottom(xScale))
      .selectAll("text")
      .attr("transform", "rotate(-45)")
      .style("text-anchor", "end");

    // Bars
    const chartGroup = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);
    chartGroup
      .selectAll("g")
      .data(series)
      .join("g")
      .attr("fill", d => color(d.key)!)
      .selectAll("rect")
      .data(d => d.map(point => ({ ...point, key: d.key })))
      .join("rect")
      .attr("x", d => xScale(String(d.data.fiscal_year))!)
      .attr("y", d => yScale(d[1]))
      .attr("height", d => yScale(d[0]) - yScale(d[1]))
      .attr("width", xScale.bandwidth());

    const tooltip = d3
      .select("body")
      .append("div")
      .attr("class", "tooltip")
      .style("opacity", 0)
      .style("position", "absolute")
      .style("pointer-events", "none")
      .style("background-color", "#fff")
      .style("border", "1px solid #ccc")
      .style("padding", "12px")
      .style("font-size","18px")
      .style("border-radius", "6px");

    chartGroup
      .selectAll("g")
      .selectAll("rect")
      .on("mouseover", (event, d: any) => {
        tooltip.transition().duration(100).style("opacity", 0.9);
        tooltip.html(`
          <b>Year: ${d.data.fiscal_year}</b><br>
          Category: <span class="badge bg-secondary">${formatEntityString(d.key)}</span><br>
          Value: <span class="badge bg-light text-dark">${formatCurrency(d[1] - d[0])}</span>
        `);
        // Dim all bars
        svg.selectAll("rect").transition().duration(100).style("opacity", 0.2);

        // Keep hovered bar fully visible
        svg.selectAll("rect")
          .filter((r: any )=> r.data.fiscal_year === d.data.fiscal_year)
          .transition().duration(100)
          .style("opacity", 0.6);

        // Make the hovered section even brighter
        d3.select(event.currentTarget)
          .transition().duration(100)
          .style("opacity", 1);
      })
      .on("mousemove", event => tooltip.style("left", event.pageX + 10 + "px").style("top", event.pageY - 28 + "px"))
      .on("mouseleave", () => {
        tooltip.transition().duration(100).style("opacity", 0);

        // Reset all rects to full opacity
        svg.selectAll("rect")
          .transition()
          .duration(100)
          .style("opacity", 1);
      });

    // Render legend under chart
    if (legendRef.current) {
      const legendDiv = d3.select(legendRef.current);
      legendDiv.selectAll("*").remove(); 

      const legendItems = legendDiv
        .selectAll(".legend-item")
        .data(categories)
        .enter()
        .append("div")
        .attr("class", "legend-item")
        .style("display", "inline-flex")
        .style("align-items", "center")
        .style("margin-right", "12px")
        .style("margin-bottom", "4px");

      legendItems
        .append("div")
        .style("width", "16px")
        .style("height", "16px")
        .style("background-color", d => color(d)!)
        .style("margin-right", "4px")
        .style("border-radius", "3px");

      legendItems
        .append("div")
        .text(d => d)
        .style("font-size", "14px");
    }

  }, [data, selectedOptions, loading]);

  // Updated - Initialize Tabulator only if tab is active to avoid verticalFillMode issues
  useEffect(() => {
    if (loading || !tableRef.current || activeTab !== "expense") return; // only init if tab visible

    // Destroy old instance if it exists
    if (tabulatorRef.current) {
      tabulatorRef.current.destroy();
      tabulatorRef.current = null;
    }

    Tabulator.registerModule([PageModule, DownloadModule, ExportModule, SortModule]);

    const mobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) && window.innerWidth < 1025;

    setTimeout(() => {
      const filteredData = data
      .filter(d => selectedOptions.includes(d.agg1))
      .sort((a, b) => b.fiscal_year - a.fiscal_year);

      tabulatorRef.current = new Tabulator(tableRef.current, {
        layout: mobile ? "fitDataFill" : "fitColumns",
        pagination: "local",
        paginationSize: 10,
        columns: [
          {
            titleFormatter: () => {
              const element = document.createElement("span");
              element.innerHTML = "<i class='fas fa-landmark'></i> Entity Name";
              return element;
            },
            field: "entity_name",
            titleDownload: "Entity Name",
          },
          {
            titleFormatter: () => {
              const element = document.createElement("span");
              element.innerHTML = "<i class='fas fa-calendar'></i> Fiscal Year";
              return element;
            },
            field: "fiscal_year",
            titleDownload: "Fiscal Year",
          },
          {
            titleFormatter: () => {
              const element = document.createElement("span");
              element.innerHTML = "<i class='fas fa-dollar-sign'></i> " +
                chartLevel.charAt(0).toUpperCase() + chartLevel.slice(1);
              return element;
            },
            field: "agg1",
            sorter: "string",
            titleDownload: chartLevel.charAt(0).toUpperCase() + chartLevel.slice(1),
          },
          {
            titleFormatter: () => {
              const element = document.createElement("span");
              element.innerHTML = "<i class='fas fa-dollar-sign'></i> Net Amount";
              return element;
            },
            field: "net_amount",
            sorter: "number",
            formatter: (cell: any) => formatCurrency(cell.getValue()),
            titleDownload: "Net Amount",
          },
        ],
        data: filteredData,
        initialSort: [],
      });
    }, 0);

    const handleResize = () => {
      if (!tabulatorRef.current) return;
      const mobileResize = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) && window.innerWidth < 1025;
      tabulatorRef.current.setLayout(mobileResize ? "fitDataFill" : "fitColumns");
      tabulatorRef.current.redraw(true);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [loading, activeTab, data, selectedOptions, chartLevel]);

  // Update table whenever data or selectedOptions change
  useEffect(() => {
    if (!tabulatorRef.current) return;
    const filteredData = data.filter(d => selectedOptions.includes(d.agg1));
    tabulatorRef.current.replaceData(filteredData);
  }, [data, selectedOptions]);

  const handleDownload = (type: "csv" | "xlsx" | "html" | "json") => {  
    const formattedEntityName = entityName?.toLowerCase()?.replaceAll(" ", "_") || "Entity";
    if (!tabulatorRef.current) return;
    tabulatorRef.current.download(type, `${formattedEntityName}_${chartLevel}_expenses.${type}`);
  };

  return (
    <>
      {chartLevel === "category" ? (
        <Row className="mb-2">
          <Col xs={12}>
            <Card bg="secondary" text="white" style={{ border: "none", borderRadius: "0" }}>
              <Card.Body style={{ justifyItems: "center" }}>
                <div className="tu_tabHeader">
                  <div className="tu-tabHeader__titleRow">
                    <h1 className="tu-tabHeader__title">
                      Expense Analysis</h1>
                      <button
                        type="button"
                        className="tu-tabHeader__infoBtn"
                        onClick={() => setShowHeaderInfo(!showHeaderInfo)}
                        aria-label="Entity Expenses info"
                      >
                        <i className="fas fa-info"/>
                      </button>
                    </div>
                  </div>

                  <Collapse in={showHeaderInfo}>
                    <div>
                      <p className="lead mt-2 text-center">
                        The expense analysis tab allows you to view an entity's expenses in context of the entity’s expense funds, organizations, and categories.
                      </p>
                      <p className="lead mt-2 text-center">
                        You can view the data in a bar, line, or pie graph, or view in table format. The data is also available to download.
                      </p>
                      <ul className="text-center p-0" style={{ listStylePosition: 'inside' }}>
                        <li>Expense Analysis - analyze expense by fund, organizational heirarchy, or category</li>
                      </ul>
                    </div>
                  </Collapse>
                {/* </Row> */}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      ) : <></>}

      {data.length > 0 ? (
        <Card className="mb-4">
          <CardHeader style={{ textTransform: "uppercase" }}>{chartLevel.charAt(0).toUpperCase() + chartLevel.slice(1)} Analysis</CardHeader>

          <Row className="py-3 px-4 no-overflow-row">
            <Col xs={12} md={4}>
            <MultiSelectAsync
              value={selectedOptions.map(o => ({ value: o, label: o }))}
              onChange={(selected) => setSelectedOptions(selected ? selected.map(s => s.value) : [])}
              isSearchable
              loadOptions={async (inputValue) => {
                return options
                  .filter(o => o.toLowerCase().includes(inputValue.toLowerCase()))
                  .map(o => ({ value: o, label: o }));
              }}
              defaultOptions={options.map(o => ({ value: o, label: o }))}
              closeMenuOnSelect={false}
              options={options.map(o => ({ value: o, label: o }))}
            />
            </Col>
          </Row>

          {/* Updated container */}
          <div id="chart-container" ref={containerRef} style={{ width: "100%", padding: "20px" }}>
            {/* Below added to implement download functionality for D3 chart */}
            <div style={{ display: "flex", gap: "10px", marginBottom: "10px" }}>
              <div
                className="btn btn-secondary"
                id="btn-chart-download"
                onClick={() => {
                  if (!chartRef.current) return;
                  const currentEntityName = entityName || data.find(d => d.entity_id === selectedEntityId)?.entity_name || "Entity";

                  // Serialize and download the chart
                  const serializer = new XMLSerializer();
                  const svgString = serializer.serializeToString(chartRef.current);
                  const canvas = document.createElement('canvas');
                  const context = canvas.getContext('2d');

                  const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
                  const url = URL.createObjectURL(svgBlob);

                  const img = new Image();
                  img.onload = () => {
                    canvas.width = img.width;
                    canvas.height = img.height;
                    if (context) {
                      context.fillStyle = "white";
                      context.fillRect(0, 0, canvas.width, canvas.height);

                      context.drawImage(img, 0, 0);
                    }
                    // context?.drawImage(img, 0, 0);
                    URL.revokeObjectURL(url);

                    const pngUrl = canvas.toDataURL('image/png');
                    const a = document.createElement('a');
                    const formattedName = currentEntityName.toLowerCase().replaceAll(" ", "_");
                    a.download = `${formattedName}_${chartLevel}_expenses.png`;
                    a.href = pngUrl;
                    a.click();
                  };
                  img.src = url;
                }}
                style={{ margin: 0 }}
              >
                Download
              </div>
            </div>

            <svg
              ref={chartRef}
              width="100%"
              height={containerRef.current ? containerRef.current.clientWidth * 0.6 : 600}
              viewBox={`0 0 ${containerRef.current?.clientWidth ?? 1200} ${containerRef.current?.clientWidth ? containerRef.current.clientWidth * 0.6 : 600}`}
              preserveAspectRatio="xMidYMid meet"
            />
            <div style={{ marginTop: "12px", fontWeight: "bold", fontSize: "16px", textAlign: "center" }}>
              {data.find(d => d.entity_id === selectedEntityId)?.entity_name ?? "Entity"}
            </div>
            <div ref={legendRef} style={{ marginTop: "12px", display: "flex", flexWrap: "wrap" }} />
          </div>

          <Row className="py-4 px-4">
            <Col>
              <div className="mb-2 d-flex gap-2">
                <button className="btn btn-secondary" onClick={() => handleDownload("csv")}>CSV</button>
                <button className="btn btn-secondary" onClick={() => handleDownload("xlsx")}>Excel</button>
                <button className="btn btn-secondary" onClick={() => handleDownload("html")}>HTML</button>
                <button className="btn btn-secondary" onClick={() => handleDownload("json")}>JSON</button>
              </div>

              <div ref={tableRef} className="table-light" style={{ minHeight: 400 }} />
            </Col>
          </Row>
        </Card>
      ) : (
        <Card className="mb-4">
          <CardHeader style={{ textTransform: "uppercase" }}>{chartLevel.charAt(0).toUpperCase() + chartLevel.slice(1)} Revenue</CardHeader>
          <div className="p-5 text-center">
            <div className="alert alert-light shadow-sm" role="status">
              No data available
            </div>
          </div>
        </Card>
      )}
    </>
  );
};

export default EntityExpensesChartAndTable;